import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import apiClient from '../lib/api'

export const useAcampantesStore = defineStore('acampantes', () => {
  const acampantes = ref([])
  const loading = ref(false)

  const totalCount = computed(() => acampantes.value.length)
  const pending = computed(() => acampantes.value.filter(a => !a.check))
  const confirmed = computed(() => acampantes.value.filter(a => a.check))
  const pendingCount = computed(() => pending.value.length)
  const confirmedCount = computed(() => confirmed.value.length)

  function byEquipe(equipe) {
    return acampantes.value.filter(a => a.equipe === equipe)
  }

  async function fetchAll() {
    loading.value = true
    try {
      const res = await apiClient.get('/listar')
      acampantes.value = res.data
    } finally {
      loading.value = false
    }
  }

  async function create(data) {
    const res = await apiClient.post('/cadastrar', data)
    addFromPusher(res.data.acampante)
    return res.data
  }

  async function update(id, data) {
    const res = await apiClient.patch(`/atualizar/${id}`, data)
    updateFromPusher(res.data.acampante)
    return res.data
  }

  async function remove(id) {
    await apiClient.delete(`/remover/${id}`)
    removeFromPusher(id)
  }

  async function checkin(id, { transporte }) {
    const res = await apiClient.post(`/scan/${id}`, { transporte })
    // API retorna participant atualizado (no caso de alreadyCheckedIn, ainda
    // vem o estado canônico). Sincroniza o store via dedupe.
    if (res.data.participant) updateFromPusher(res.data.participant)
    return res.data
  }

  async function uncheckin(id) {
    const res = await apiClient.post(`/uncheck/${id}`)
    if (res.data.participant) updateFromPusher(res.data.participant)
    return res.data
  }

  // Pagamentos. Não há evento Pusher pra pagamento — atualizamos o acampante
  // local refazendo o cálculo de valorPago/falta.
  async function addPagamento(acampanteId, { valor, data, observacao }) {
    const res = await apiClient.post(`/acampantes/${acampanteId}/pagamentos`, { valor, data, observacao })
    const idx = acampantes.value.findIndex(a => a.id === acampanteId)
    if (idx !== -1 && res.data.pagamento) {
      const a = acampantes.value[idx]
      const novosPagamentos = [...(a.pagamentos || []), res.data.pagamento]
        .sort((x, y) => new Date(x.data) - new Date(y.data))
      const valorPago = Math.round(novosPagamentos.reduce((s, p) => s + Number(p.valor), 0) * 100) / 100
      const falta = a.valorTotal != null ? Math.max(0, Math.round((Number(a.valorTotal) - valorPago) * 100) / 100) : null
      acampantes.value[idx] = { ...a, pagamentos: novosPagamentos, valorPago, falta }
    }
    return res.data
  }

  async function removePagamento(acampanteId, pagamentoId) {
    await apiClient.delete(`/pagamentos/${pagamentoId}`)
    const idx = acampantes.value.findIndex(a => a.id === acampanteId)
    if (idx !== -1) {
      const a = acampantes.value[idx]
      const novosPagamentos = (a.pagamentos || []).filter(p => p.id !== pagamentoId)
      const valorPago = Math.round(novosPagamentos.reduce((s, p) => s + Number(p.valor), 0) * 100) / 100
      const falta = a.valorTotal != null ? Math.max(0, Math.round((Number(a.valorTotal) - valorPago) * 100) / 100) : null
      acampantes.value[idx] = { ...a, pagamentos: novosPagamentos, valorPago, falta }
    }
  }

  // Dedupe por id: usado também pelos eventos Pusher
  function addFromPusher(item) {
    if (!item || !item.id) return
    const idx = acampantes.value.findIndex(a => a.id === item.id)
    if (idx === -1) acampantes.value.push(item)
    else acampantes.value[idx] = item
  }

  function updateFromPusher(item) {
    if (!item || !item.id) return
    const idx = acampantes.value.findIndex(a => a.id === item.id)
    if (idx === -1) acampantes.value.push(item)
    else acampantes.value[idx] = item
  }

  function removeFromPusher(id) {
    acampantes.value = acampantes.value.filter(a => a.id !== id)
  }

  return {
    acampantes,
    loading,
    totalCount,
    pending,
    confirmed,
    pendingCount,
    confirmedCount,
    byEquipe,
    fetchAll,
    create,
    update,
    remove,
    checkin,
    uncheckin,
    addPagamento,
    removePagamento,
    addFromPusher,
    updateFromPusher,
    removeFromPusher
  }
})
